import { useState, useEffect } from "react";
import { FolderEditIcon, Loader2Icon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NoteVault } from "@/lib/types";
import { useUpdateNoteVault } from "@/hooks/queries/useUpdateNoteVault";

export function RenameVaultDialog({
  open,
  onOpenChange,
  vault,
  onRenameVault,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vault: NoteVault | null;
  onRenameVault: (name: string) => void;
}) {
  const [vaultName, setVaultName] = useState("");

  const updateVault = useUpdateNoteVault();

  useEffect(() => {
    if (vault) {
      setVaultName(vault.name);
    }
  }, [vault]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (vaultName.trim() && vault) {
      onRenameVault(vaultName.trim());
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setVaultName("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderEditIcon className="h-5 w-5" />
            <span>Rename Vault</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vault-name" className="text-sm font-medium">
              Vault Name
            </Label>
            <Input
              id="vault-name"
              value={vaultName}
              onChange={e => setVaultName(e.target.value)}
              placeholder="Enter vault name..."
              className="w-full"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !vaultName.trim() ||
                vaultName === vault?.name ||
                updateVault.isPending
              }
            >
              {updateVault.isPending ? (
                <>
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                  Renaming...
                </>
              ) : (
                "Rename Vault"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
